import React, { useMemo, useState } from 'react';
import {
  Box,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  useColorModeValue
} from '@chakra-ui/react';
import { FaCalendarCheck, FaMoneyBillWave } from 'react-icons/fa';
import { ActivityProductPayment } from '../models/activities-fees-by-member-response';
import { MemberProductStatus } from '../models/periodic-product-payment';
import ProductFeeCard from './product-fee-card';

interface Props {
  activityId: string;
  cardTitle: string;
  activitiesFeesYears: string[];
  activitiesFeesPayments: Map<string, ActivityProductPayment[]>;
  feesToPayPayments: MemberProductStatus[];
  onCheckboxChange: (activityId: string, fee: MemberProductStatus) => void;
}

function ActivityPaymentHistory(props: Props): JSX.Element {
  const [tabIndex, setTabIndex] = useState(0);

  const textColor = useColorModeValue('gray.800', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.300');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const yearsWithData = useMemo(() => {
    return props.activitiesFeesYears
      .filter((year) => {
        const activityFees = props.activitiesFeesPayments
          .get(year)
          ?.find((activityFee) => activityFee.activityId === props.activityId);
        return (activityFees?.productPayments?.length ?? 0) > 0;
      })
      .sort((a, b) => Number(a) - Number(b));
  }, [props.activitiesFeesYears, props.activitiesFeesPayments, props.activityId]);

  const getAllActivityProducts = (): MemberProductStatus[] => {
    const result: MemberProductStatus[] = [];
    props.activitiesFeesPayments.forEach((activityFees) => {
      const activityFee = activityFees.find((fee) => fee.activityId === props.activityId);
      if (activityFee) {
        result.push(...activityFee.productPayments);
      }
    });
    return result;
  };

  const getActivityFeesForYear = (year: string): MemberProductStatus[] => {
    return (
      props.activitiesFeesPayments
        .get(year)
        ?.find((activityFee) => activityFee.activityId === props.activityId)
        ?.productPayments ?? []
    );
  };

  if (yearsWithData.length === 0) {
    return (
      <Box
        bg={cardBg}
        p={6}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        boxShadow="lg"
      >
        <VStack spacing={4} align="stretch">
          <HStack>
            <Icon as={FaMoneyBillWave} color="green.500" />
            <Heading as="h3" size="md" color={textColor}>
              Historial de pagos
            </Heading>
          </HStack>
          <Text color={secondaryTextColor}>No hay pagos registrados para esta actividad.</Text>
        </VStack>
      </Box>
    );
  }

  const allActivityProducts = getAllActivityProducts();

  return (
    <Box
      bg={cardBg}
      p={6}
      borderRadius="lg"
      border="1px solid"
      borderColor={borderColor}
      boxShadow="lg"
    >
      <VStack spacing={4} align="stretch">
        <HStack>
          <Icon as={FaMoneyBillWave} color="green.500" />
          <Heading as="h3" size="md" color={textColor}>
            Historial de pagos
          </Heading>
        </HStack>

        <Tabs
          variant="soft-rounded"
          colorScheme="green"
          isFitted
          index={tabIndex}
          onChange={setTabIndex}
        >
          <TabList flexWrap="wrap" gap={2} mb={2}>
            {yearsWithData.map((year) => (
              <Tab key={year}>
                <Icon as={FaCalendarCheck} mr={2} />
                {year}
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {yearsWithData.map((year) => (
              <TabPanel key={year} px={0} py={6}>
                <SimpleGrid columns={[1, 2, 3, 4]} spacing={6}>
                  {getActivityFeesForYear(year).map((activityFee) => (
                    <ProductFeeCard
                      key={`${activityFee.product.id}-${year}`}
                      productFee={activityFee}
                      cardTitle={props.cardTitle}
                      variant="activity"
                      toPayList={props.feesToPayPayments}
                      allProducts={allActivityProducts}
                      onChange={(fee) => props.onCheckboxChange(props.activityId, fee)}
                    />
                  ))}
                </SimpleGrid>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </VStack>
    </Box>
  );
}

export default ActivityPaymentHistory;
